import { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from './useApi';

const POLL_INTERVAL_MS = 60000; // 60 seconds

export function useNavBadges() {
  const { apiCall } = useApi();
  const [badges, setBadges] = useState({
    tasks_overdue: 0,
    deals_no_next_step: 0,
    quotes_pending: 0,
    deal_candidates_new: 0,
  });
  const [loading, setLoading] = useState(true);
  const initialFetchDone = useRef(false);

  const fetchBadges = useCallback(async () => {
    if (document.hidden) return;

    try {
      const result = await apiCall('nav-badges');
      const next = {
        tasks_overdue: result.tasks_overdue || 0,
        deals_no_next_step: result.deals_no_next_step || 0,
        quotes_pending: result.quotes_pending || 0,
        deal_candidates_new: result.deal_candidates_new || 0,
      };
      // Preserve object identity when counts are unchanged so consumers (the
      // whole ~240-element Sidebar) don't re-render on every 60s poll / tab focus.
      setBadges(prev =>
        (prev.tasks_overdue === next.tasks_overdue &&
          prev.deals_no_next_step === next.deals_no_next_step &&
          prev.quotes_pending === next.quotes_pending &&
          prev.deal_candidates_new === next.deal_candidates_new)
          ? prev
          : next
      );
    } catch {
      // Silently ignore polling errors — badges are non-critical
    } finally {
      if (!initialFetchDone.current) {
        initialFetchDone.current = true;
        setLoading(false);
      }
    }
  }, [apiCall]);

  // Initial fetch + polling
  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  // Re-fetch when tab becomes visible again
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) fetchBadges();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchBadges]);

  return { badges, loading, refetch: fetchBadges };
}
